import { FASTAPI_CONFIG } from '@/config/fastapi'
import { api } from '@/scripts/api'

import type { ShareListResponse, ShareResponse } from '../types/share'

export interface ShareListParams {
  page?: number
  page_size?: number
  is_public?: boolean
  user_id?: number
  tag_ids?: string
  order_by?: 'created_at' | 'like_count' | 'view_count'
  order_desc?: boolean
  redirect_share_id?: number
  share_source_types?: number[]
  platform?: string
}

export class ShareService {
  /**
   * 获取分享列表
   */
  static async getShares(
    params: ShareListParams = {}
  ): Promise<ShareListResponse> {
    const searchParams = new URLSearchParams()

    if (params.page) searchParams.append('page', params.page.toString())
    if (params.page_size)
      searchParams.append('page_size', params.page_size.toString())
    if (params.is_public !== undefined)
      searchParams.append('is_public', params.is_public.toString())
    if (params.user_id)
      searchParams.append('user_id', params.user_id.toString())
    if (params.tag_ids) searchParams.append('tag_ids', params.tag_ids)
    if (params.order_by) searchParams.append('order_by', params.order_by)
    if (params.order_desc !== undefined)
      searchParams.append('order_desc', params.order_desc.toString())
    if (params.redirect_share_id !== undefined) {
      searchParams.append(
        'redirect_share_id',
        params.redirect_share_id.toString()
      )
    }
    if (params.share_source_types && params.share_source_types.length > 0) {
      searchParams.append(
        'share_source_types',
        params.share_source_types.join(',')
      )
    }
    if (params.platform) {
      searchParams.append('platform', params.platform)
    }

    console.log('ShareService请求URL:', `/shares/works?${searchParams}`)
    // 直接使用FastAPI的shares路由，而不是通过comfyui路由
    const response = await fetch(
      `${FASTAPI_CONFIG.BASE_URL}/api/v1/shares/works?${searchParams}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await api.getAuthToken()}`
        }
      }
    )

    if (!response.ok) {
      throw new Error(`获取分享列表失败: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * 获取分享详情
   */
  static async getShare(shareId: number): Promise<ShareResponse> {
    const response = await fetch(
      `${FASTAPI_CONFIG.BASE_URL}/api/v1/shares/work?share_id=${shareId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await api.getAuthToken()}`
        }
      }
    )

    if (!response.ok) {
      throw new Error(`获取分享详情失败: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * 获取分享详情（别名方法）
   */
  static async getShareDetail(shareId: number): Promise<ShareResponse> {
    return this.getShare(shareId)
  }

  /**
   * 增加浏览量
   */
  static async incrementViewCount(shareId: number): Promise<void> {
    const response = await api.fetchApi(
      `/shares/work/view?share_id=${shareId}`,
      {
        method: 'POST'
      }
    )

    if (!response.ok) {
      console.warn(`增加浏览量失败: ${response.statusText}`)
    }
  }

  /**
   * 根据 prompt_execution_id 获取工作流数据
   */
  static async getWorkflowByExecutionId(executionId: number): Promise<any> {
    const response = await api.fetchApi(
      `/prompt/executions/detail?execution_id=${executionId}`
    )

    if (!response.ok) {
      throw new Error(`获取工作流数据失败: ${response.statusText}`)
    }

    const executionData = await response.json()

    // 从 prompt_data.extra_data.extra_pnginfo.workflow 中提取工作流数据
    if (
      executionData.prompt_data &&
      executionData.prompt_data.extra_data &&
      executionData.prompt_data.extra_data.extra_pnginfo &&
      executionData.prompt_data.extra_data.extra_pnginfo.workflow
    ) {
      const workflowData =
        executionData.prompt_data.extra_data.extra_pnginfo.workflow

      // 确保工作流数据格式正确
      if (workflowData && typeof workflowData === 'object') {
        // 确保有必要的字段
        if (!workflowData.version) {
          workflowData.version = '0.4'
        }
        if (!workflowData.nodes) {
          workflowData.nodes = []
        }

        return workflowData
      }
    }

    throw new Error('工作流数据不存在或格式不正确')
  }

  /**
   * 点赞分享
   */
  static async likeShare(shareId: number): Promise<void> {
    const response = await api.fetchApi(
      `/shares/work/like?share_id=${shareId}`,
      {
        method: 'POST'
      }
    )

    if (!response.ok) {
      throw new Error(`点赞失败: ${response.statusText}`)
    }
  }

  /**
   * 取消点赞分享
   */
  static async unlikeShare(shareId: number): Promise<void> {
    const response = await api.fetchApi(
      `/shares/work/like?share_id=${shareId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )

    if (!response.ok) {
      const errorText = await response.text()
      console.error(
        '取消点赞失败:',
        response.status,
        response.statusText,
        errorText
      )
      throw new Error(`取消点赞失败: ${response.statusText}`)
    }
  }

  /**
   * 收藏分享
   */
  static async favoriteShare(shareId: number): Promise<void> {
    const response = await api.fetchApi(
      `/shares/work/favorite?share_id=${shareId}`,
      {
        method: 'POST'
      }
    )

    if (!response.ok) {
      throw new Error(`收藏失败: ${response.statusText}`)
    }
  }

  /**
   * 取消收藏分享
   */
  static async unfavoriteShare(shareId: number): Promise<void> {
    const response = await api.fetchApi(
      `/shares/work/favorite?share_id=${shareId}`,
      {
        method: 'DELETE'
      }
    )

    if (!response.ok) {
      throw new Error(`取消收藏失败: ${response.statusText}`)
    }
  }

  /**
   * 更新分享信息
   */
  static async updateShare(
    shareId: number,
    updateData: {
      title?: string
      description?: string
      tag_ids?: number[]
      is_public?: boolean
    }
  ): Promise<ShareResponse> {
    const response = await api.fetchApi(`/shares/work?share_id=${shareId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateData)
    })

    if (!response.ok) {
      throw new Error(`更新分享失败: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * 删除分享
   */
  static async deleteShare(shareId: number): Promise<void> {
    const response = await api.fetchApi(`/shares/work?share_id=${shareId}`, {
      method: 'DELETE'
    })

    if (!response.ok) {
      throw new Error(`删除分享失败: ${response.statusText}`)
    }
  }
}
